import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { Modal } from 'antd';
import { PUBLIC_AUTH_PATHS } from 'constants/app-info';

interface GuardText {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
}

/** Cờ trong state của mục lịch sử "chốt" (xem mục 2 bên dưới). */
const SENTINEL = '__hpLeaveGuard';

// Số trang đang giữ chốt. React.StrictMode (src/index.js) chạy effect hai lần ở dev: gỡ → gắn lại
// ngay, nên việc dọn mục chốt được hoãn một nhịp và chỉ làm khi không còn ai giữ.
let holders = 0;

/** Trang xác thực công khai (/login…): đi tới đó là phiên đã bị huỷ, không còn lưu được gì. */
const isPublicAuthPath = (path: string) =>
  PUBLIC_AUTH_PATHS.includes(path.replace(/\/+$/, '') || '/');

/**
 * Phiên đăng nhập đã bị xoá ở trình duyệt: Đăng xuất (redux/auth/actions.ts) và hết phiên
 * (utils/axios-utils.ts → về /login) đều xoá mục SESSION trước khi rời trang.
 */
const sessionGone = () => {
  try {
    const raw = window.localStorage.getItem('SESSION');
    return !raw || !JSON.parse(raw)?.accessToken;
  } catch {
    // Không đọc được (bị chặn, hỏng): coi như phiên còn, vẫn hỏi như bình thường.
    return false;
  }
};

/**
 * Hỏi lại trước khi rời trang lúc còn thay đổi chưa lưu.
 *
 * Trang Quản trị dùng <BrowserRouter> (src/index.js) nên không có useBlocker của data router.
 * Thay vào đó chặn ở ba lối ra:
 *  1. Bấm link / menu / ô "Tìm chức năng" / navigate(): mọi đường đều gọi navigator.push|replace
 *     lấy từ NavigationContext, nên bọc hai hàm đó trong lúc `active`.
 *  2. Nút Back của trình duyệt: không chặn được `popstate` trước router (listener trên `window`
 *     chạy theo thứ tự đăng ký, router đăng ký trước — đã đo trên Chrome 142). Nên khi bắt đầu có
 *     thay đổi, đẩy thêm MỘT mục lịch sử "chốt" trùng địa chỉ trang. Bấm Back chỉ lùi về mục
 *     thật của chính trang này (trang không đổi), lúc đó đặt chốt lại ngay rồi mới hỏi: ở lại
 *     thì thôi, rời trang thì lùi qua chốt và mục thật (2 bước).
 *  3. Tải lại / đóng tab: `beforeunload` (trình duyệt tự hiện câu hỏi của nó).
 * Đổi tham số trên cùng trang (vd. `?tab=`) thì không hỏi. Về trang đăng nhập (Đăng xuất, hết
 * phiên) cũng không hỏi: lúc đó phiên đã bị huỷ, "Ở lại" chỉ giữ một bản nháp không lưu được.
 */
export function useLeaveGuard(active: boolean, text: GuardText) {
  const { navigator } = useContext(NavigationContext);
  const textRef = useRef(text);
  textRef.current = text;

  useEffect(() => {
    if (!active) return undefined;
    const nav = navigator as any;
    const herePath = window.location.pathname;
    const originalPush = nav.push;
    const originalReplace = nav.replace;
    let released = false;
    let asking = false;
    // Hộp hỏi đang mở. Modal.confirm tĩnh sống ngoài cây React: thôi chặn (rời trang bằng lối
    // khác, lưu xong…) thì phải tự đóng, không để nó nằm lại trên trang khác.
    let dialog: ReturnType<typeof Modal.confirm> | null = null;

    const onSentinel = () => window.history.state?.usr?.[SENTINEL] === true;
    const stillHere = () => window.location.pathname === herePath;
    const placeSentinel = () => {
      if (!stillHere() || onSentinel()) return;
      originalPush(
        {
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        },
        { [SENTINEL]: true }
      );
    };

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // Hết phiên / đăng xuất rồi mới tải lại về /login: bản nháp không lưu được nữa, đừng hỏi.
      if (sessionGone()) return;
      event.preventDefault();
      // Chrome cần returnValue mới hiện hộp hỏi.
      event.returnValue = '';
    };

    const release = () => {
      if (released) return;
      released = true;
      asking = false;
      dialog?.destroy();
      dialog = null;
      nav.push = originalPush;
      nav.replace = originalReplace;
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPop);
    };

    const ask = (leave: () => void, stay?: () => void) => {
      if (asking) return;
      asking = true;
      dialog = Modal.confirm({
        title: textRef.current.title,
        content: textRef.current.content,
        okText: textRef.current.okText,
        cancelText: textRef.current.cancelText,
        okButtonProps: { danger: true },
        // antd tự đóng hộp sau onOk / onCancel. Hộp của một lần chặn đã kết thúc (đã rời trang)
        // thì không làm gì nữa: đừng lùi lịch sử hay đặt chốt trên trang khác.
        onOk: () => {
          dialog = null;
          asking = false;
          if (released || !stillHere()) return;
          release();
          leave();
        },
        onCancel: () => {
          dialog = null;
          asking = false;
          if (released || !stillHere()) return;
          stay?.();
        },
      });
    };

    function onPop() {
      // Lùi từ mục chốt về mục thật của trang này. Lùi thẳng sang trang khác thì router đã đổi
      // trang, không còn gì để hỏi.
      if (released || !stillHere() || onSentinel()) return;
      // Mục chốt vừa bị dùng mất: đặt lại NGAY, không đợi trả lời. Bấm Back thêm lần nữa khi hộp
      // hỏi đang mở (bấm Back hai lần liền, hay vừa bấm menu) vẫn chỉ lùi về mục thật của trang
      // này, không rời trang mà mất bản nháp; hộp đang mở giữ nguyên chờ trả lời.
      placeSentinel();
      if (asking) return;
      // Rời trang: lùi qua chốt vừa đặt và mục thật của trang này (2 bước).
      ask(() => window.history.go(onSentinel() ? -2 : -1));
    }

    const pathOf = (to: any): string => {
      if (typeof to === 'string') return to.split(/[?#]/)[0];
      return typeof to?.pathname === 'string' ? to.pathname : '';
    };

    const guarded =
      (original: (...args: any[]) => void) =>
      (to: any, ...rest: any[]) => {
        const next = pathOf(to);
        if (released || !next || next === window.location.pathname) {
          original(to, ...rest);
          return;
        }
        // Đang đứng trên mục chốt thì THAY nó bằng trang mới, để Back từ trang mới về đúng trang này.
        const go = () => (onSentinel() ? originalReplace(to, ...rest) : original(to, ...rest));
        // Về /login (Đăng xuất gọi navigate sau khi đã huỷ phiên): thôi chặn và đi luôn, không hỏi.
        if (isPublicAuthPath(next)) {
          release();
          go();
          return;
        }
        ask(go);
      };

    nav.push = guarded(originalPush);
    nav.replace = guarded(originalReplace);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('popstate', onPop);
    holders += 1;
    placeSentinel();

    return () => {
      const leaving = released;
      release();
      holders -= 1;
      if (leaving) return;
      // Đã lưu / bỏ thay đổi: gỡ mục chốt để nút Back trở lại bình thường.
      window.setTimeout(() => {
        if (holders === 0 && stillHere() && onSentinel()) window.history.back();
      }, 0);
    };
  }, [active, navigator]);
}
